import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CILogo } from '../components/CILogo';
import { FileText, Shield, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <CILogo size={40} showText={true} theme="light" />
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          <p className="text-gray-600">
            Last Updated: December 11, 2024
          </p>
        </div>

        {/* Content Sections */}
        <div className="prose prose-lg max-w-none space-y-8">
          
          {/* Acceptance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using Commerce Index ("Service"), a product of Dispatch Global Services Inc (d/b/a DGS or Sagisu) ("Company," "we," "our," or "us"), 
              at <strong>commerceindex.com</strong> or <strong>commerceindex.com</strong>, 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              These Terms apply to all users, including merchants, consumers, developers, and visitors.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>"Company"</strong>: Dispatch Global Services Inc (d/b/a DGS or Sagisu)</li>
              <li><strong>"Service"</strong>: The Commerce Index platform, including website, mobile apps, APIs, and Shopify app</li>
              <li><strong>"Merchant"</strong>: Business users who post deals and manage products</li>
              <li><strong>"Consumer"</strong>: End-users who browse and purchase deals</li>
              <li><strong>"Deal"</strong>: A product with a temporary discount or promotion</li>
              <li><strong>"CI Score"</strong>: Our proprietary 0-1000 scoring system for deal quality</li>
              <li><strong>"Content"</strong>: Any data, text, images, or information posted on the Service</li>
            </ul>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration & Eligibility</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Eligibility</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You must be at least 18 years old to use the Service</li>
              <li>You must provide accurate and complete registration information</li>
              <li>You must be authorized to bind your business (for merchant accounts)</li>
              <li>One account per email address</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">3.2 Account Security</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>You are liable for all activities under your account</li>
              <li>Do not share your login credentials with others</li>
            </ul>
          </section>

          {/* Subscription Plans */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Plans & Billing</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Pricing Tiers</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="space-y-2 text-gray-700">
                <li><strong>FREE Plan</strong>: $0/month - 10 products, 5 deals/month, basic analytics</li>
                <li><strong>PRO Plan</strong>: $29/month - Unlimited products/deals, 10 AI recommendations/month, Shopify sync</li>
                <li><strong>INTELLIGENCE SUITE</strong>: $99/month - Everything + AI competitive analysis, price intelligence</li>
                <li><strong>ENTERPRISE</strong>: Custom pricing - API access, white label, SLA guarantees</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Billing Terms</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Subscriptions are billed monthly or annually in advance</li>
              <li>Annual plans receive 20% discount</li>
              <li>14-day free trial for new subscribers (credit card required)</li>
              <li>Billing handled securely by Stripe</li>
              <li>Automatic renewal unless cancelled before period end</li>
              <li>No refunds for partial months</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Cancellation</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You may cancel anytime from your account settings</li>
              <li>Cancellation takes effect at end of current billing period</li>
              <li>You retain access until period end</li>
              <li>Downgrading to FREE plan applies limits immediately</li>
            </ul>
          </section>

          {/* Merchant Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Merchant Responsibilities</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">You agree to:</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Post accurate product information (title, price, availability)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Honor all deals posted on the platform</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Update or remove expired/invalid deals promptly</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Comply with all applicable laws and regulations</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Respect consumer privacy and data protection laws</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">You agree NOT to:</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Post fake, misleading, or fraudulent deals</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Inflate prices before discounting (fake discounts)</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Use the Service for illegal activities</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Scrape or reverse-engineer the platform</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Circumvent subscription limits or access controls</span>
              </li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Our IP</h3>
            <p className="text-gray-700">
              Commerce Index, CI Score, our logo, and all proprietary algorithms are owned by Dispatch Global Services Inc (d/b/a DGS or Sagisu). 
              You may not copy, modify, or distribute our intellectual property without written permission.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">6.2 Your Content</h3>
            <p className="text-gray-700">
              You retain ownership of content you post (product data, deal information). By posting content, you grant us 
              a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content to provide the Service.
            </p>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Service Availability</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We strive for 99.9% uptime (Enterprise SLA)</li>
              <li>Scheduled maintenance will be announced in advance</li>
              <li>We reserve the right to modify or discontinue features</li>
              <li>No guarantee of uninterrupted service</li>
              <li>We are not liable for third-party service outages (Shopify, Stripe, etc.)</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 uppercase font-bold mb-2">
                <AlertCircle className="w-5 h-5 inline mr-2" />
                IMPORTANT LEGAL NOTICE
              </p>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, DISPATCH GLOBAL SERVICES INC (D/B/A DGS OR SAGISU) SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY 
                OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                Our total liability for any claims shall not exceed the amount you paid us in the 12 months prior to the claim.
              </p>
            </div>
          </section>

          {/* Warranty Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Warranty Disclaimer</h2>
            <p className="text-gray-700">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. We do not guarantee:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
              <li>CI Score accuracy or deal quality</li>
              <li>AI recommendation performance or revenue results</li>
              <li>Shopify integration uptime or data sync reliability</li>
              <li>Compatibility with all devices or browsers</li>
              <li>Error-free or uninterrupted service</li>
            </ul>
          </section>

          {/* Prohibited Uses */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Prohibited Uses</h2>
            <p className="text-gray-700 mb-4">You may not use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Violate any laws or regulations</li>
              <li>Infringe intellectual property rights</li>
              <li>Transmit malware, viruses, or harmful code</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Spam or send unsolicited communications</li>
              <li>Manipulate CI Scores or gaming the system</li>
              <li>Create multiple accounts to bypass subscription limits</li>
              <li>Access other merchants' data without authorization</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">11.1 By You</h3>
            <p className="text-gray-700">
              You may terminate your account anytime from account settings. Your data will be deleted within 30 days.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">11.2 By Us</h3>
            <p className="text-gray-700 mb-3">
              We may suspend or terminate your account if you:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Violate these Terms</li>
              <li>Fail to pay subscription fees</li>
              <li>Post fraudulent or illegal content</li>
              <li>Engage in abusive behavior</li>
              <li>Use the Service for prohibited purposes</li>
            </ul>
            <p className="text-gray-700 mt-3">
              We will provide 7 days notice before termination (unless immediate action required for legal/security reasons).
            </p>
          </section>

          {/* API Usage */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. API Usage Terms</h2>
            <p className="text-gray-700 mb-4">
              If you use our API (Enterprise plan):
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Rate Limits</strong>: 100K requests/month on Enterprise plan</li>
              <li><strong>Authentication</strong>: API keys must be kept secure</li>
              <li><strong>Attribution</strong>: Credit Commerce Index when displaying CI Scores</li>
              <li><strong>Caching</strong>: Cache responses for max 1 hour</li>
              <li><strong>Abuse</strong>: Excessive requests may result in throttling or termination</li>
            </ul>
          </section>

          {/* Shopify Integration */}
          <section className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Shopify Integration Terms</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.1 Authorization</h3>
            <p className="text-gray-700 mb-3">
              By connecting your Shopify store, you authorize us to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Read your product catalog, prices, and inventory</li>
              <li>Create discount codes and price rules on your behalf</li>
              <li>Sync data in real-time between Shopify and Commerce Index</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">13.2 Your Responsibilities</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Maintain active Shopify subscription</li>
              <li>Ensure product data is accurate</li>
              <li>Review auto-generated discount codes before they go live</li>
              <li>You can disconnect Shopify anytime from your dashboard</li>
            </ul>
          </section>

          {/* AI Recommendations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. AI Recommendations Disclaimer</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed">
                AI-generated deal recommendations are suggestions only, not guarantees. We do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
                <li>Revenue increases or conversion improvements</li>
                <li>Accuracy of predicted CI Scores</li>
                <li>Market trend forecasts</li>
              </ul>
              <p className="text-gray-700 mt-3">
                You are solely responsible for pricing decisions. Commerce Index is not liable for lost revenue from 
                following AI recommendations.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify and hold Dispatch Global Services Inc (d/b/a DGS or Sagisu) and Commerce Index harmless from any claims, damages, or expenses arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Content you post on the platform</li>
            </ul>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Dispute Resolution</h2>
            <p className="text-gray-700 mb-3">
              Any disputes shall be resolved through:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li><strong>Informal Resolution</strong>: Contact support@commerceindex.com first</li>
              <li><strong>Mediation</strong>: If informal resolution fails, binding mediation</li>
              <li><strong>Arbitration</strong>: Individual arbitration (no class actions)</li>
              <li><strong>Jurisdiction</strong>: Courts of San Francisco, California, USA</li>
            </ol>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Changes to Terms</h2>
            <p className="text-gray-700">
              We may modify these Terms at any time. Material changes will be communicated via:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
              <li>Email notification to registered users</li>
              <li>Prominent notice on the website</li>
              <li>Updated "Last Updated" date at top of page</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Continued use after changes constitutes acceptance. If you disagree, you must stop using the Service.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Governing Law</h2>
            <p className="text-gray-700">
              These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email</strong>: <a href="mailto:legal@commerceindex.com" className="text-blue-600 hover:underline">legal@commerceindex.com</a></p>
              <p><strong>Support</strong>: <a href="mailto:support@commerceindex.com" className="text-blue-600 hover:underline">support@commerceindex.com</a></p>
              <p><strong>Company</strong>: Dispatch Global Services Inc (d/b/a DGS or Sagisu)</p>
              <p><strong>Product</strong>: Commerce Index</p>
              <p><strong>Address</strong>: Dispatch Global Services Inc., 123 Tech Street, San Francisco, CA 94105, USA</p>
            </div>
          </section>

          {/* Entire Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Entire Agreement</h2>
            <p className="text-gray-700">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Dispatch Global Services Inc (d/b/a DGS or Sagisu) 
              regarding use of the Commerce Index service, superseding any prior agreements.
            </p>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex gap-6">
          <button 
            onClick={() => navigate('/privacy')}
            className="text-blue-600 hover:underline flex items-center gap-2"
          >
            <Shield className="w-4 h-4" />
            Privacy Policy
          </button>
          <button 
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Home
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-600">
          <p>© 2024 Dispatch Global Services Inc (d/b/a DGS or Sagisu). All rights reserved.</p>
          <p className="mt-2">
            Commerce Index is a product of Dispatch Global Services Inc.
            By using this service, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </footer>
    </div>
  );
}
